import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Collapse } from '@mui/material';
import { useForm } from 'react-hook-form';
import DocumentUploadSection from '../../common/FileUpload/DocumentUploadSection';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch, useSelector } from 'react-redux';
import { selectFiles, updateFileField } from '../../../Store/userSlice';
import { base64ToFile } from '../../common/Util/Util';
import { bankDetailsDocValidation } from '../../common/ValidationRules/validationRules';

const BankDetailsDocUploadForm = React.forwardRef((props, ref) => {
  const files = useSelector(selectFiles);
  const dispatch = useDispatch();
  const [bankStatements, setBankStatements] = useState([{ id: 1, isRequired: true }]);
  const [isBankDetailsExpanded, setIsBankDetailsExpanded] = useState(false);
  const [slLoans, setSlLoans] = useState([{ id: 1, isRequired: true }]);
  const [isSlLoanExpanded, setIsSlLoanExpanded] = useState(false);

  const {
    control,
    formState: { errors },
    trigger,
    setValue,
    unregister
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      bankStatement:[],
      slLoan:[]
    }
  });

  const convertAndSetfile = (fieldName, fileData) => {
    if (fileData && fileData.base64 && fileData.filename) {
      const fileObj = base64ToFile(fileData.base64, fileData.filename);
      return fileObj;
    } else if (fileData && fileData.remote === true && (fileData.url || fileData.documentPath)) {
      return fileData;
    } else {
      return null;
    }
  };

  // Helper function to handle bank statement file uploads
  const handleBankStatementFileSelect = (index) => async (selectedFile) => {
    try {
      if (!selectedFile) {
        const currentFiles = Array.isArray(files.bankStatement) ? [...files.bankStatement] : [];
        if (currentFiles.length > index) {
          currentFiles[index] = null;
          dispatch(updateFileField({
            field: 'bankStatement',
            value: currentFiles
          }));
        }
        return;
      }
      
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const fileData = {
            base64: base64Data,
            filename: selectedFile.name,
          };
        const currentFiles = Array.isArray(files.bankStatement) ? [...files.bankStatement] : [];
        while (currentFiles.length <= index) {
          currentFiles.push(null);
        }
        currentFiles[index] = fileData;
          
          dispatch(updateFileField({
            field: 'bankStatement',
            value: currentFiles
          }));
        };
        reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Failed to process file:', error);
    }
  };
  
  // Helper function to handle SL loan file uploads
  const handleSlLoanFileSelect = (index) => async (selectedFile) => {
    try {
      if (!selectedFile) {
        const currentFiles = Array.isArray(files.slLoan) ? [...files.slLoan] : [];
        if (currentFiles.length > index) {
          currentFiles[index] = null;
          dispatch(updateFileField({
            field: 'slLoan',
            value: currentFiles
          }));
        }
        return;
      }
      
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const fileData = {
            base64: base64Data,
            filename: selectedFile.name,
          };
          
        const currentFiles = Array.isArray(files.slLoan) ? [...files.slLoan] : [];
        while (currentFiles.length <= index) {
          currentFiles.push(null);
        }
        currentFiles[index] = fileData;
        
          dispatch(updateFileField({
            field: 'slLoan',
            value: currentFiles
          }));
        };
        reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Failed to process file:', error);
    }
  };

  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      bankStatements.forEach((bank, index) => {
        const fieldName = bank.id === 1 ? 'bankStatement' : `bankStatement${bank.id}`;
        const fieldValue = index < (files.bankStatement?.length || 0) ? files.bankStatement[index] : null;
        
        if (!fieldValue) {
          setValue(fieldName, null);
        }
      });
      
      slLoans.forEach((loan, index) => {
        const fieldName = loan.id === 1 ? 'slLoan' : `slLoan${loan.id}`;
        const fieldValue = index < (files.slLoan?.length || 0) ? files.slLoan[index] : null;
        
        if (!fieldValue) {
          setValue(fieldName, null);
        }
      });
      
      const result = await trigger();
      return result;
    }
  }));

  useEffect(() => {
    // Set bank statements if available
    if (Array.isArray(files.bankStatement)) {
      // Update the state to match the number of bank statements
      if (files.bankStatement.length > bankStatements.length) {
        const newBankStatements = [...bankStatements];
        for (let i = bankStatements.length; i < files.bankStatement.length; i++) {
          newBankStatements.push({ id: i + 1, isRequired: i === 0 });
        }
        setBankStatements(newBankStatements);
      }
      
      // Set values for each bank statement
      files.bankStatement.forEach((file, index) => {
        if (file) {
          setValue(index === 0 ? 'bankStatement' : `bankStatement${index + 1}`, convertAndSetfile("bankStatement", file));
        }
      });
    }

    // Set SL loans if available
    if (Array.isArray(files.slLoan)) {
      // Update the state to match the number of SL loans
      if (files.slLoan.length > slLoans.length) {
        const newSlLoans = [...slLoans];
        for (let i = slLoans.length; i < files.slLoan.length; i++) {
          newSlLoans.push({ id: i + 1, isRequired: i === 0 });
        }
        setSlLoans(newSlLoans);
      }
      
      // Set values for each SL loan
      files.slLoan.forEach((file, index) => {
        if (file) {
          setValue(index === 0 ? 'slLoan' : `slLoan${index + 1}`, convertAndSetfile("slLoan", file));
        }
      });
    }
  }, [files, setValue]);

  const handleAddBank = () => {
    const newId = bankStatements.length + 1;
    setBankStatements(prev => [...prev, { id: newId, isRequired: false }]);
  };

  const handleRemoveBank = (id) => {
    if (id === 1) return;
    
    // Remove from state
    setBankStatements(prev => prev.filter(bank => bank.id !== id));
    unregister(`bankStatement${id}`);
    
    // Remove from Redux store
    const bankIndex = id - 1;
    if (Array.isArray(files.bankStatement) && files.bankStatement.length > bankIndex) {
      const newBankStatements = [...files.bankStatement];
      newBankStatements.splice(bankIndex, 1);
      dispatch(updateFileField({
        field: 'bankStatement',
        value: newBankStatements
      }));
    }
  };

  const handleAddSlLoan = () => {
    const newId = slLoans.length + 1;
    setSlLoans(prev => [...prev, { id: newId, isRequired: true }]);
  };

  const handleRemoveSlLoan = (id) => {
    if (id === 1) return;
    
    // Remove from state
    setSlLoans(prev => prev.filter(loan => loan.id !== id));
    unregister(`slLoan${id}`);
    
    // Remove from Redux store
    const loanIndex = id - 1;
    if (Array.isArray(files.slLoan) && files.slLoan.length > loanIndex) {
      const newSlLoans = [...files.slLoan];
      newSlLoans.splice(loanIndex, 1);
      dispatch(updateFileField({
        field: 'slLoan',
        value: newSlLoans
      }));
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        Step 3/4
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon sx={{ color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          Upload files in PDF format only, up to 5 MB in size. Scanned PDFs are not accepted.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            onClick={() => setIsBankDetailsExpanded(!isBankDetailsExpanded)}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#eeeeee' }
            }}
          >
            <Typography variant="h6">
              Upload Bank Details Documents
            </Typography>
            {isBankDetailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>

          <Collapse in={isBankDetailsExpanded}>
            <Box sx={{ p: 3 }}>
              {bankStatements.map((bank, index) => (
                <Box key={bank.id} sx={{ mb: 2, position: 'relative' }}>
                  <DocumentUploadSection
                    control={control}
                    title={`Last 12 Month Statement Bank - ${bank.id} (Optional)`}
                    name={bank.id === 1 ? 'bankStatement' : `bankStatement${bank.id}`}
                    setValue={setValue}
                    validationRule={bank.isRequired ? bankDetailsDocValidation.bankStatement : bankDetailsDocValidation.bankStatementOptional}
                    errors={errors?.[bank.id === 1 ? 'bankStatement' : `bankStatement${bank.id}`]}
                    helptext={errors?.[bank.id === 1 ? 'bankStatement' : `bankStatement${bank.id}`]?.message}
                    value={Array.isArray(files?.bankStatement) && files?.bankStatement[bank.id - 1] ? 
                      convertAndSetfile("bankStatement", files?.bankStatement[bank.id - 1]) : null}
                    onFileSelect={handleBankStatementFileSelect(bank.id - 1)}
                  />
                  {!bank.isRequired && (
                    <Button
                      onClick={() => handleRemoveBank(bank.id)}
                      color="error"
                      variant="text"
                      size="small"
                      sx={{ position: 'absolute', top: 8, right: 16, zIndex: 2 }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={handleAddBank}
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              >
                Add more Bank Documents
              </Button>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            onClick={() => setIsSlLoanExpanded(!isSlLoanExpanded)}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#eeeeee' }
            }}
          >
            <Typography variant="h6">
              Upload Latest SL Loan Details Documents
            </Typography>
            {isSlLoanExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={isSlLoanExpanded}>
            <Box sx={{ p: 3 }}>
              {slLoans.map((loan, idx) => (
                <Box key={loan.id} sx={{ mb: 2, position: 'relative' }}>
                  <DocumentUploadSection
                    control={control}
                    title={`Latest SL Loan - ${loan.id} (Optional)`}
                    name={loan.id === 1 ? 'slLoan' : `slLoan${loan.id}`}
                    setValue={setValue}
                    validationRule={loan.isRequired ? bankDetailsDocValidation.slLoanDetails : bankDetailsDocValidation.bankStatementOptional}
                    errors={errors?.[loan.id === 1 ? 'slLoan' : `slLoan${loan.id}`]}
                    helptext={errors?.[loan.id === 1 ? 'slLoan' : `slLoan${loan.id}`]?.message}
                    value={Array.isArray(files?.slLoan) && files?.slLoan[loan.id - 1] ? 
                      convertAndSetfile("slLoan", files?.slLoan[loan.id - 1]) : null}
                    onFileSelect={handleSlLoanFileSelect(loan.id - 1)}
                  />
                  {!loan.isRequired && (
                    <Button
                      onClick={() => handleRemoveSlLoan(loan.id)}
                      color="error"
                      variant="text"
                      size="small"
                      sx={{ position: 'absolute', top: 8, right: 16, zIndex: 2 }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddSlLoan}
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              >
                Add SL Loan
              </Button>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
});

export default BankDetailsDocUploadForm;